<template>
  <UCard
    class="page-search-filter card rounded-[16px] ring-0"
    :class="ui?.root"
    :ui="{
      body: ui?.body,
    }"
  >
    <UForm ref="form" @submit="emit('search', queryParams)">
      <div class="mb-5 flex w-full h-12 justify-between gap-2.5 max-md:mb-0">
        <UInput
          v-model="query"
          class="w-full"
          :class="ui?.searchInput"
          icon="i-local-search"
          :placeholder="placeholder"
          :ui="{
            base: 'h-full',
          }"
        />

        <DirectionsDropdown
          v-model="directions"
          :class="ui?.directionsDropdown"
        />

        <LessonTypesDropdown
          v-if="lessonTypes"
          v-model="lessonTypes"
          class="max-lg:hidden"
          :class="ui?.lessonTypesDropdown"
        />

        <UButton
          class="min-w-31.25 typo-paragraph-md-medium! max-md:hidden gap-3"
          :class="ui?.searchButton"
          leading-icon="i-local-search"
          :ui="{
            leadingIcon: 'h-6 w-6',
          }"
          @click="form?.submit"
        >
          Найти
        </UButton>
      </div>

      <ClientOnly>
        <div class="flex items-center justify-between gap-4 max-md:hidden">
          <div
            class="flex items-center max-h-6.5 gap-2"
            :class="ui?.filterBadges"
          >
            <UBadge
              v-for="(direction, index) in directions.slice(0, maxBadges)"
              :key="index"
              class="px-3 typo-paragraph-sm-regular! bg-background-bg1 text-button-secondary-foreground ring-transparent!"
            >
              {{ direction.name }}
            </UBadge>

            <template v-if="directions?.length > maxBadges">
              <div class="ellips" />
              <UBadge
                class="px-3 typo-paragraph-sm-regular! bg-background-bg1 text-button-secondary-foreground ring-transparent!"
              >
                +{{ directions.length - maxBadges }}
              </UBadge>
            </template>
            <template v-if="lessonTypes?.length">
              <div v-if="directions.length" class="ellips" />
              <UBadge
                v-for="(direction, index) in lessonTypes"
                :key="index"
                class="px-3 typo-paragraph-sm-regular! bg-background-bg1 text-button-secondary-foreground ring-transparent!"
              >
                {{ direction.name }}
              </UBadge>
            </template>
            <UButton
              v-if="isClearFilterBtnVisible"
              class="ml-2 typo-paragraph-sm-medium!"
              :class="ui?.clearFilterButton"
              icon="i-local-cross"
              variant="ghost"
              :ui="{
                leadingIcon: 'text-accent-default fill-accent-default',
              }"
              size="xs"
              @click="clearFilter"
            >
              Очистить фильтр
            </UButton>
          </div>
          <div
            class="min-h-6.5 typo-paragraph-sm-regular leading-6.5 text-neutral-light max-lg:hidden"
            :class="ui?.counter"
          >
            Найдено {{ allDirections.length }} направлений
          </div>
        </div>
      </ClientOnly>
    </UForm>
  </UCard>
</template>

<script lang="ts" setup>
import type { DirectionMenuItem } from '~/types'
import LessonTypesDropdown from './LessonTypesDropdown.vue'
import type { LessonTypeRef } from '~/api/index.js'

defineOptions({
  name: 'PageSearchFilter',
})

const emit = defineEmits<{
  search: [
    {
      query?: string
      direction_id?: string | null
      lesson_type_id?: string | null
    },
  ]
}>()

withDefaults(
  defineProps<{
    placeholder?: string
    ui?: {
      root?: string
      body?: string
      searchInput?: string
      directionsDropdown?: string
      lessonTypesDropdown?: string
      searchButton?: string
      filterBadges?: string
      clearFilterButton?: string
      counter?: string
    }
  }>(),
  {
    placeholder: 'Моковый поиск по направлению',
    ui: () => ({}),
  },
)

const breakpoints = useBreakpointsStore()

const form = useTemplateRef('form')

const directions = defineModel<DirectionMenuItem[]>('directions', {
  required: true,
})

const lessonTypes = defineModel<LessonTypeRef[]>('lessonTypes')

const query = defineModel<string>('query', {
  required: true,
})

const queryParams = computed(() => {
  const acc: Record<string, any> = {}
  if (query.value) {
    acc.q = query.value
  }

  if (directions.value?.length) {
    acc.direction_id = directions.value.map(({ id }) => id)
  }

  if (lessonTypes.value?.length) {
    acc.lesson_type_id = lessonTypes.value.map(({ id }) => id)
  }

  return acc
})

const directoryStore = useDirectoryStore()
const { directions: allDirections } = storeToRefs(directoryStore)

const isClearFilterBtnVisible = computed(
  () => !!directions.value.length || !!query.value.length,
)

const maxBadges = computed(() => {
  if (breakpoints.isTablet) {
    return 3
  }

  return 4
})

const clearFilter = () => {
  directions.value = []
  lessonTypes.value = []
  query.value = ''
  emit('search', {})
}
</script>

<style lang="scss" scoped>
.card {
  box-shadow:
    0px 4px 20px -2px #51627805,
    0px 2px 24px 0px #5162780f;
}

.ellips {
  width: 4px;
  height: 4px;
  border-radius: 50%;
  background-color: var(--color-neutral-light);
}
</style>
